import { useMemo } from 'react';
import { Select, type SelectProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAllTenantsWithCompanies } from '@/hooks/useAllTenantsWithCompanies';

export interface CompanySelectProps extends Omit<SelectProps<string | string[]>, 'options' | 'loading'> {
  /** Only companies of this tenant are offered; the select is disabled until it is set */
  clientId?: string;
  /** Company IDs to leave out (e.g. companies the user already belongs to) */
  excludeIds?: string[];
}

/**
 * Searchable company picker scoped to one tenant, for admin forms. Supports `mode="multiple"`.
 */
export function CompanySelect({ clientId, excludeIds = [], placeholder, disabled, ...selectProps }: CompanySelectProps) {
  const { t } = useTranslation('admin');
  const { tenantsById, isLoading } = useAllTenantsWithCompanies();

  const options = useMemo(() => {
    const companies = clientId ? tenantsById.get(clientId)?.companies ?? [] : [];
    return companies
      .filter((company) => !excludeIds.includes(company.id))
      .map((company) => ({ value: company.id, label: company.name }));
  }, [tenantsById, clientId, excludeIds]);

  return (
    <Select
      showSearch
      optionFilterProp="label"
      loading={isLoading}
      disabled={disabled || !clientId}
      placeholder={placeholder ?? t('tenantPickers.companyPlaceholder')}
      notFoundContent={isLoading ? undefined : t('tenantPickers.noCompanies')}
      {...selectProps}
      options={options}
    />
  );
}
